const ECDH_ALGORITHM = { name: "ECDH", namedCurve: "P-256" };
const AES_ALGORITHM = { name: "AES-GCM", length: 256 };
const PROFILE_FIELDS = ["displayName", "avatarId", "personalAvatarId", "user_color"];

function getCrypto() {
  const cryptoApi = globalThis.crypto;
  if (!cryptoApi?.subtle) {
    throw new Error("Web Crypto is required for device link encryption.");
  }

  return cryptoApi;
}

function getTextEncoder() {
  if (typeof TextEncoder === "undefined") {
    throw new Error("TextEncoder is required for device link encryption.");
  }

  return new TextEncoder();
}

function getTextDecoder() {
  if (typeof TextDecoder === "undefined") {
    throw new Error("TextDecoder is required for device link encryption.");
  }

  return new TextDecoder();
}

function base64Url(bytes) {
  const binary = Array.from(bytes, byte => String.fromCharCode(byte)).join("");
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
}

function base64UrlDecode(value) {
  const normalized = String(value || "").replace(/-/g, "+").replace(/_/g, "/");
  const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, "=");
  const binary = atob(padded);
  const bytes = new Uint8Array(binary.length);

  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }

  return bytes;
}

async function importPublicKey(publicJwk) {
  return getCrypto().subtle.importKey("jwk", publicJwk, ECDH_ALGORITHM, true, []);
}

async function deriveEncryptionKey(privateKey, publicKey) {
  return getCrypto().subtle.deriveKey({ name: "ECDH", public: publicKey }, privateKey, AES_ALGORITHM, false, [
    "encrypt",
    "decrypt"
  ]);
}

export async function generateDeviceLinkEncryptionKeyPair() {
  return getCrypto().subtle.generateKey(ECDH_ALGORITHM, true, ["deriveKey"]);
}

export async function exportDeviceLinkPublicKey(keyPair) {
  return getCrypto().subtle.exportKey("jwk", keyPair.publicKey);
}

export async function encryptDeviceLinkPayload(publicJwk, payload) {
  const targetPublicKey = await importPublicKey(publicJwk);
  const ephemeralKeyPair = await generateDeviceLinkEncryptionKeyPair();
  const ephemeralPublicJwk = await exportDeviceLinkPublicKey(ephemeralKeyPair);
  const encryptionKey = await deriveEncryptionKey(ephemeralKeyPair.privateKey, targetPublicKey);
  const iv = new Uint8Array(12);
  getCrypto().getRandomValues(iv);

  const ciphertext = await getCrypto().subtle.encrypt(
    { name: "AES-GCM", iv },
    encryptionKey,
    getTextEncoder().encode(JSON.stringify(payload))
  );

  return base64Url(
    getTextEncoder().encode(
      JSON.stringify({
        v: 1,
        alg: "ECDH-ES+A256GCM",
        epk: ephemeralPublicJwk,
        iv: base64Url(iv),
        ciphertext: base64Url(new Uint8Array(ciphertext))
      })
    )
  );
}

export async function decryptDeviceLinkPayload(privateKey, encryptedPayload) {
  const envelope = JSON.parse(getTextDecoder().decode(base64UrlDecode(encryptedPayload)));
  const ephemeralPublicKey = await importPublicKey(envelope.epk);
  const encryptionKey = await deriveEncryptionKey(privateKey, ephemeralPublicKey);

  const plaintext = await getCrypto().subtle.decrypt(
    { name: "AES-GCM", iv: base64UrlDecode(envelope.iv) },
    encryptionKey,
    base64UrlDecode(envelope.ciphertext)
  );

  return JSON.parse(getTextDecoder().decode(plaintext));
}

export function getDeviceLinkRoomPath(locationLike = window.location) {
  return `${locationLike.pathname}${locationLike.search || ""}`;
}

export function buildDeviceLinkHandoffPayload(store, roomPath) {
  const payload = { path: roomPath };
  const profile = store?.state?.profile || {};

  if (store?.state?.activity?.hasChangedName) {
    const copiedProfile = {};
    for (const field of PROFILE_FIELDS) {
      if (profile[field] !== undefined && profile[field] !== null) {
        copiedProfile[field] = profile[field];
      }
    }

    if (Object.keys(copiedProfile).length > 0) {
      payload.profile = copiedProfile;
    }
  }

  return payload;
}
